import { ParticipantFullSummary } from "../types/participant.types";
import { RegistrationRequest } from "../types/registration.types";
import { AddressRequest, PaymentRequest } from "../types/address.types";
import { ActivityChoice } from "../types/activity.types";
import { AccomodationRequest } from "../types/accomodation.types";
import { PresentationRequest } from "../types/presentation.types";
import { PartecipantFeeRequest, EscortFeeRequest } from "../types/fee.types";
import { ParticipantRequest } from "../types/participant.types";
import { formatDateForInput } from "./date-formatter";
import { isEqual } from "lodash";
import { ConferenceConfiguration } from "../types/conference.types";

/**
 * Deep comparison of arrays ignoring order
 * Uses lodash isEqual for deep comparison but handles array order differences
 */
function deepEqualArrays(a: any[] | null, b: any[] | null): boolean {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;

  // Sort arrays by a stable key for comparison
  // For ActivityChoice: use activityId
  // For PresentationRequest: use presentationTypeId or first property
  const sortedA = [...a].sort((x, y) => {
    const keyA =
      x.activityId ??
      x.presentationTypeId ??
      (typeof x === "object" ? JSON.stringify(x) : String(x));
    const keyB =
      y.activityId ??
      y.presentationTypeId ??
      (typeof y === "object" ? JSON.stringify(y) : String(y));
    return String(keyA).localeCompare(String(keyB));
  });
  const sortedB = [...b].sort((x, y) => {
    const keyA =
      x.activityId ??
      x.presentationTypeId ??
      (typeof x === "object" ? JSON.stringify(x) : String(x));
    const keyB =
      y.activityId ??
      y.presentationTypeId ??
      (typeof y === "object" ? JSON.stringify(y) : String(y));
    return String(keyA).localeCompare(String(keyB));
  });
  return sortedA.every((item, index) => isEqual(item, sortedB[index]));
}

/**
 * Converts ParticipantFullSummary to RegistrationRequest format
 * Used for editing existing participant data
 */
export const participantToRegistrationRequest = (
  participant: ParticipantFullSummary,
  configJson: ConferenceConfiguration | null | undefined,
): RegistrationRequest => {
  const participantValuesByFieldId = new Map<number, string | number>();
  participant.customFieldValues.forEach((field) => {
    participantValuesByFieldId.set(field.fieldId, field.valueText);
  });

  const addressValuesByFieldId = new Map<number, string | number>();
  participant.address?.customFieldValues?.forEach((field) => {
    addressValuesByFieldId.set(field.fieldId, field.valueText);
  });

  const paymentValuesByFieldId = new Map<number, string | number>();
  participant.payment?.customFieldValues?.forEach((field) => {
    paymentValuesByFieldId.set(field.fieldId, field.valueText);
  });

  // Extract name parts from fullName
  const nameParts = participant.fullName?.split(" ") || [];
  const firstName = nameParts.length > 0 ? nameParts[0] : "";
  const surName = nameParts.slice(1).join(" ") || "";

  // Convert address
  const address: AddressRequest = {
    addressType: participant.address?.addressType || "NONE",
    city: participant.address?.city || "",
    street: participant.address?.street || "",
    postalCode: participant.address?.postalCode || "",
    country: participant.address?.country || "",
    taxNumber: participant.address?.taxNumber || "",
    phone: participant.address?.phone || "",
    valuesByFieldId: addressValuesByFieldId,
  };

  // Convert payment
  const payment: PaymentRequest = {
    payment: participant.payment?.payment,
    city: participant.payment?.city || "",
    street: participant.payment?.street || "",
    postalCode: participant.payment?.postalCode || "",
    country: participant.payment?.country || "",
    taxNumber: participant.payment?.taxNumber || "",
    email: participant.payment?.email || "",
    phone: participant.payment?.phone || "",
    fullName: participant.payment?.fullName || "",
    valuesByFieldId: paymentValuesByFieldId,
  };

  // Convert meals - preserve null if original was null
  const meals: ActivityChoice[] | null = participant.meals
    ? participant.meals.map((meal) => ({
        activityId: meal.activityId,
        count: meal.choiceCount,
      }))
    : null;

  // Convert professionals - preserve null if original was null
  const professionals: ActivityChoice[] | null = participant.professionals
    ? participant.professionals.map((prof) => ({
        activityId: prof.activityId,
        count: prof.choiceCount,
      }))
    : null;

  // Convert socials - preserve null if original was null
  const socials: ActivityChoice[] | null = participant.socials
    ? participant.socials.map((social) => ({
        activityId: social.activityId,
        count: social.choiceCount,
      }))
    : null;

  const hotelBooking: AccomodationRequest | null = participant.hotelBooking
    ? {
        hotelId: participant.hotelBooking.hotelId,
        roomId: participant.hotelBooking.roomId,
        arrivalDate: formatDateForInput(participant.hotelBooking.arrivalDate),
        departureDate: formatDateForInput(
          participant.hotelBooking.departureDate,
        ),
        noNights: participant.hotelBooking.numberOfNights,
        roomMate: participant.hotelBooking.roomMateName || undefined,
      }
    : null;

  // Convert presentations - preserve null if original was null
  // Note: PresentationSummary has presentationType and presentationTopic as strings (names),
  // not IDs. We can't convert them here without access to presentation lists.
  // This will be handled in EditParticipantPage when setting initialValues.
  const presentations: PresentationRequest[] | null = null; // Will be set in EditParticipantPage

  // Convert participant fee – use participantFeeId from summary so form and update use correct id
  const participantFee: PartecipantFeeRequest | null =
    participant.participationFee || configJson?.participantFeeEnabled
      ? {
          participationFeeId: participant.participationFee?.participantFeeId,
        }
      : null;

  // Convert escort fee – use participantFeeId from summary, plus numberOfAttendants and attendantsName
  const escortFee: EscortFeeRequest | null =
    participant.escortFee || configJson?.participantFeeEnabled
      ? {
          participationFeeId: participant.escortFee?.participantFeeId,
          numberOfAttendants: participant.escortFee?.numberOfAttendants ?? 0,
          attendantsName: participant.escortFee?.attendantsName ?? "",
        }
      : null;

  // Convert participant register
  const participantRegister: ParticipantRequest = {
    title: participant.title,
    firstName: firstName,
    surName: surName,
    workplace: participant.workplace || "",
    email: participant.email || "",
    valuesByFieldId: participantValuesByFieldId,
  };

  return {
    participantRegister,
    address: configJson?.mailingDetailsEnabled ? address : null,
    payment: configJson?.paymentDetailsEnabled ? payment : null,
    // Preserve empty arrays as empty arrays, not null
    // Only set to null if the original was null
    meals: configJson?.lunchEnabled && meals !== null ? meals : null,
    professionals:
      configJson?.activityEnabled && professionals !== null
        ? professionals
        : null,
    socials: configJson?.activityEnabled && socials !== null ? socials : null,
    hotelBooking:
      configJson?.accommodationEnabled && hotelBooking !== null
        ? hotelBooking
        : null,
    presentations:
      configJson?.presentationEnabled && presentations !== null
        ? presentations
        : null,
    participantFee:
      configJson?.participantFeeEnabled && participantFee !== null
        ? participantFee
        : null,
    escortFee:
      configJson?.participantFeeEnabled && escortFee !== null
        ? escortFee
        : null,
    congressEventId: participant.congressEvent.id,
    comment: participant.comment || "",
  };
};

/**
 * Calculates differences between original and modified participant data
 */
export interface ParticipantChanges {
  [key: string]: {
    original: any;
    modified: any;
  };
}

export const calculateParticipantChanges = (
  original: ParticipantFullSummary,
  modified: RegistrationRequest,
): ParticipantChanges => {
  const changes: ParticipantChanges = {};

  // Compare participant register fields
  const nameParts = original.fullName?.split(" ") || [];
  const originalFirstName = nameParts.length > 0 ? nameParts[0] : "";
  const originalSurName = nameParts.slice(1).join(" ") || "";

  if (modified.participantRegister.firstName !== originalFirstName) {
    changes["participantRegister.firstName"] = {
      original: originalFirstName,
      modified: modified.participantRegister.firstName,
    };
  }
  if (modified.participantRegister.surName !== originalSurName) {
    changes["participantRegister.surName"] = {
      original: originalSurName,
      modified: modified.participantRegister.surName,
    };
  }
  if (modified.participantRegister.workplace !== original.workplace) {
    changes["participantRegister.workplace"] = {
      original: original.workplace,
      modified: modified.participantRegister.workplace,
    };
  }
  if (modified.participantRegister.email !== original.email) {
    changes["participantRegister.email"] = {
      original: original.email,
      modified: modified.participantRegister.email,
    };
  }
  if (modified.participantRegister.title !== original.title) {
    changes["participantRegister.title"] = {
      original: original.title,
      modified: modified.participantRegister.title,
    };
  }

  // Compare address fields
  if (original.address || modified.address) {
    if (modified.address?.addressType !== original.address?.addressType) {
      changes["address.addressType"] = {
        original: original.address?.addressType,
        modified: modified.address?.addressType,
      };
    }
    if (modified.address?.city !== original.address?.city) {
      changes["address.city"] = {
        original: original.address?.city,
        modified: modified.address?.city,
      };
    }
    if (modified.address?.street !== original.address?.street) {
      changes["address.street"] = {
        original: original.address?.street,
        modified: modified.address?.street,
      };
    }
    if (modified.address?.postalCode !== original.address?.postalCode) {
      changes["address.postalCode"] = {
        original: original.address?.postalCode,
        modified: modified.address?.postalCode,
      };
    }
    if (modified.address?.country !== original.address?.country) {
      changes["address.country"] = {
        original: original.address?.country,
        modified: modified.address?.country,
      };
    }
    if (modified.address?.phone !== original.address?.phone) {
      changes["address.phone"] = {
        original: original.address?.phone,
        modified: modified.address?.phone,
      };
    }
  }

  // Compare payment fields
  if (original.payment || modified.payment) {
    if (modified.payment?.payment !== original.payment?.payment) {
      changes["payment.payment"] = {
        original: original.payment?.payment,
        modified: modified.payment?.payment,
      };
    }
    if (modified.payment?.fullName !== original.payment?.fullName) {
      changes["payment.fullName"] = {
        original: original.payment?.fullName,
        modified: modified.payment?.fullName,
      };
    }
    if (modified.payment?.city !== original.payment?.city) {
      changes["payment.city"] = {
        original: original.payment?.city,
        modified: modified.payment?.city,
      };
    }
    if (modified.payment?.street !== original.payment?.street) {
      changes["payment.street"] = {
        original: original.payment?.street,
        modified: modified.payment?.street,
      };
    }
    if (modified.payment?.postalCode !== original.payment?.postalCode) {
      changes["payment.postalCode"] = {
        original: original.payment?.postalCode,
        modified: modified.payment?.postalCode,
      };
    }
    if (modified.payment?.country !== original.payment?.country) {
      changes["payment.country"] = {
        original: original.payment?.country,
        modified: modified.payment?.country,
      };
    }
    if (modified.payment?.email !== original.payment?.email) {
      changes["payment.email"] = {
        original: original.payment?.email,
        modified: modified.payment?.email,
      };
    }
    if (modified.payment?.phone !== original.payment?.phone) {
      changes["payment.phone"] = {
        original: original.payment?.phone,
        modified: modified.payment?.phone,
      };
    }
    if (modified.payment?.taxNumber !== original.payment?.taxNumber) {
      changes["payment.taxNumber"] = {
        original: original.payment?.taxNumber,
        modified: modified.payment?.taxNumber,
      };
    }
  }

  // Compare comment
  if (modified.comment !== (original.comment || "")) {
    changes["comment"] = {
      original: original.comment || "",
      modified: modified.comment,
    };
  }

  // Compare hotelBooking - deep comparison using lodash
  // Note: original.hotelBooking doesn't have hotelId and roomId, only hotelName and hotelRoomName
  // So we only compare the fields that exist in both: arrivalDate, departureDate, numberOfNights/noNights, roomMateName/roomMate
  const normalizeValue = (val: any) => (val === undefined ? null : val);

  // Helper to check if hotelBooking is actually set (not empty/default)
  const isHotelBookingSet = (hotelBooking: any): boolean => {
    if (!hotelBooking) return false;
    // If hotelId is 0 or undefined, it's not set
    if (!hotelBooking.hotelId || hotelBooking.hotelId === 0) return false;
    return true;
  };

  const originalHasHotelBooking =
    original.hotelBooking !== null && original.hotelBooking !== undefined;
  // For modified, check if it's actually set (not just an empty object with hotelId: 0)
  const modifiedHasHotelBooking =
    modified.hotelBooking !== null &&
    modified.hotelBooking !== undefined &&
    isHotelBookingSet(modified.hotelBooking);

  if (originalHasHotelBooking !== modifiedHasHotelBooking) {
    // Existence changed
    changes["hotelBooking"] = {
      original: original.hotelBooking,
      modified: modifiedHasHotelBooking ? modified.hotelBooking : null,
    };
  } else if (
    originalHasHotelBooking &&
    modifiedHasHotelBooking &&
    original.hotelBooking &&
    modified.hotelBooking
  ) {
    // Both exist, compare the actual values (excluding hotelId and roomId which don't exist in original)
    // Normalize dates to YYYY-MM-DD format for comparison
    const normalizeDate = (date: string | Date | undefined): string | null => {
      if (!date) return null;
      try {
        return new Date(date).toISOString().split("T")[0];
      } catch {
        return String(date);
      }
    };
    const originalHotelBookingNormalized = {
      arrivalDate: normalizeDate(original.hotelBooking.arrivalDate),
      departureDate: normalizeDate(original.hotelBooking.departureDate),
      noNights: original.hotelBooking.numberOfNights,
      roomMate: normalizeValue(original.hotelBooking.roomMateName),
    };
    const modifiedHotelBookingNormalized = {
      arrivalDate: normalizeDate(modified.hotelBooking.arrivalDate),
      departureDate: normalizeDate(modified.hotelBooking.departureDate),
      noNights: modified.hotelBooking.noNights,
      roomMate: normalizeValue(modified.hotelBooking.roomMate),
    };
    if (
      !isEqual(originalHotelBookingNormalized, modifiedHotelBookingNormalized)
    ) {
      changes["hotelBooking"] = {
        original: original.hotelBooking,
        modified: modified.hotelBooking,
      };
    }
  }

  // Compare participationFee
  // Since participationFeeId is undefined in original data (set by participantToRegistrationRequest),
  // we can't reliably compare by ID. Only mark as changed if existence changed.
  const originalHasFee =
    original.participationFee !== null &&
    original.participationFee !== undefined;
  const modifiedHasFee =
    modified.participantFee !== null && modified.participantFee !== undefined;
  // Only mark as changed if the existence changed (was null, now has value, or vice versa)
  if (
    originalHasFee !== modifiedHasFee ||
    original.participationFee?.participationFeeId !==
      modified.participantFee?.participationFeeId
  ) {
    changes["participantFee"] = {
      original: original.participationFee,
      modified: modified.participantFee,
    };
  }
  // Note: We don't compare participationFeeId because original data doesn't have it,
  // and both will be undefined when form is first loaded, so they should be equal

  // Compare escortFee - deep comparison using lodash
  // Note: original.escortFee doesn't have participationFeeId, only the name
  // So we only compare numberOfAttendants and attendantsName
  // The participationFeeId comparison is handled by checking if both exist or both are null
  const originalHasEscortFee =
    original.escortFee !== null && original.escortFee !== undefined;
  const modifiedHasEscortFee =
    modified.escortFee !== null && modified.escortFee !== undefined;

  if (originalHasEscortFee !== modifiedHasEscortFee) {
    // Existence changed
    changes["escortFee"] = {
      original: original.escortFee,
      modified: modified.escortFee,
    };
  } else if (
    originalHasEscortFee &&
    modifiedHasEscortFee &&
    original.escortFee &&
    modified.escortFee
  ) {
    // Both exist, compare the actual values
    const originalEscortFeeNormalized = {
      numberOfAttendants: original.escortFee.numberOfAttendants || 0,
      attendantsName: original.escortFee.attendantsName || "",
    };
    const modifiedEscortFeeNormalized = {
      numberOfAttendants: modified.escortFee.numberOfAttendants || 0,
      attendantsName: modified.escortFee.attendantsName || "",
    };
    if (!isEqual(originalEscortFeeNormalized, modifiedEscortFeeNormalized)) {
      changes["escortFee"] = {
        original: original.escortFee,
        modified: modified.escortFee,
      };
    }
  }

  // Compare meals - deep comparison using lodash (arrays with order ignored)
  const originalMealsNormalized =
    original.meals && original.meals.length > 0
      ? original.meals.map((m) => ({
          activityId: m.activityId,
          count: m.choiceCount,
        }))
      : null;
  const modifiedMealsNormalized =
    modified.meals && modified.meals.length > 0 ? modified.meals : null;
  if (!deepEqualArrays(originalMealsNormalized, modifiedMealsNormalized)) {
    changes["meals"] = {
      original: original.meals,
      modified: modified.meals,
    };
  }

  // Compare professionals - deep comparison using lodash (arrays with order ignored)
  const originalProfessionalsNormalized =
    original.professionals && original.professionals.length > 0
      ? original.professionals.map((p) => ({
          activityId: p.activityId,
          count: p.choiceCount,
        }))
      : null;
  const modifiedProfessionalsNormalized =
    modified.professionals && modified.professionals.length > 0
      ? modified.professionals
      : null;
  if (
    !deepEqualArrays(
      originalProfessionalsNormalized,
      modifiedProfessionalsNormalized,
    )
  ) {
    changes["professionals"] = {
      original: original.professionals,
      modified: modified.professionals,
    };
  }

  // Compare socials - deep comparison using lodash (arrays with order ignored)
  const originalSocialsNormalized =
    original.socials && original.socials.length > 0
      ? original.socials.map((s) => ({
          activityId: s.activityId,
          count: s.choiceCount,
        }))
      : null;
  const modifiedSocialsNormalized =
    modified.socials && modified.socials.length > 0 ? modified.socials : null;
  if (!deepEqualArrays(originalSocialsNormalized, modifiedSocialsNormalized)) {
    changes["socials"] = {
      original: original.socials,
      modified: modified.socials,
    };
  }

  // Compare presentations - deep comparison using lodash (arrays with order ignored)
  // Note: PresentationSummary has presentationType and presentationTopic as strings (names),
  // while PresentationRequest has presentationTypeId and presentationTopicId as numbers.
  // We compare by the actual content fields that exist in both.
  const originalPresentationsNormalized =
    original.presentations && original.presentations.length > 0
      ? original.presentations.map((p) => ({
          presentationTitle: p.presentationTitle || "",
          performers: p.performers || "",
          performersWorkplace: p.performersWorkplace || "",
        }))
      : null;
  const modifiedPresentationsNormalized =
    modified.presentations && modified.presentations.length > 0
      ? modified.presentations.map((p) => ({
          presentationTitle: p.presentationTitle || "",
          performers: p.performers || "",
          performersWorkplace: p.performersWorkplace || "",
        }))
      : null;
  if (
    !deepEqualArrays(
      originalPresentationsNormalized,
      modifiedPresentationsNormalized,
    )
  ) {
    changes["presentations"] = {
      original: original.presentations,
      modified: modified.presentations,
    };
  }

  // Compare totalPrice (calculated field, but we can check if it changed)
  // This is a derived field, so we might not need to track it separately

  return changes;
};
